import { useNavigate, Link } from "react-router-dom";
import {
  Building2, PlayCircle, PauseCircle, HardHat, UserCheck, UserX, Truck, Wrench, MapPin, CalendarClock, AlertTriangle,
  Phone, Clock, ChevronRight, Euro, Bell,
} from "lucide-react";
import { useApi } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, Kpi, Loading, Empty, AvailBadge, Badge, SiteStatusBadge } from "../components/common";
import { Button } from "../components/ui/button";
import { eur, fmtDate, num, DEADLINE_LABEL } from "../lib/format";
import { BudgetAlertsBanner } from "../components/BudgetAlert";

function deadlineTone(days) {
  if (days < 0) return "bg-red-950 border-red-600 text-red-200";
  if (days <= 15) return "bg-orange-950 border-orange-500 text-orange-200";
  return "bg-slate-800 border-slate-600 text-slate-200";
}

function deadlineLabel(days) {
  if (days < 0) return `Scaduto ${-days}g`;
  if (days === 0) return "Oggi";
  return `${days} gg`;
}

export function DeadlineList({ items }) {
  if (!items?.length) return <Empty title="Nessuna scadenza imminente" />;
  return (
    <ul className="divide-y divide-border/60" data-testid="deadlines-list">
      {items.map((d) => {
        const tone = deadlineTone(d.days_left);
        return (
          <li key={`${d.ref_type}-${d.ref_id}-${d.kind}-${d.item}-${d.date}`} className="flex items-center gap-3 py-3">
            <AlertTriangle className={d.days_left <= 15 ? "h-4 w-4 text-orange-400 shrink-0" : "h-4 w-4 text-slate-500 shrink-0"} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{DEADLINE_LABEL[d.kind]} · {d.item}</div>
              <div className="text-xs text-muted-foreground truncate">{d.subject} — {fmtDate(d.date)}</div>
            </div>
            <Badge className={tone}>{deadlineLabel(d.days_left)}</Badge>
          </li>
        );
      })}
    </ul>
  );
}

function TodayTable({ rows }) {
  const nav = useNavigate();
  if (!rows?.length) return <Empty title="Nessuna attività oggi" hint="Nessun cantiere ha assegnazioni per la data odierna." />;
  return (
    <>
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm" data-testid="today-activities-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="py-2.5 pr-3">Cantiere</th><th className="pr-3">Responsabile</th><th className="pr-3 text-right">Operai</th>
              <th className="pr-3 text-right">Mezzi</th><th className="pr-3 text-right">Ore</th><th className="text-right">Costo stimato</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.site_id} onClick={() => nav(`/cantieri/${r.site_id}`)} className="border-b border-border/50 hover:bg-accent/40 cursor-pointer" data-testid={`today-row-${r.site_id}`}>
                <td className="py-3 pr-3"><div className="font-semibold">{r.site_name}</div><div className="text-xs text-muted-foreground font-mono">{r.site_code}</div></td>
                <td className="pr-3">{r.manager_name}</td>
                <td className="pr-3 text-right num">{r.worker_count}</td>
                <td className="pr-3 text-right num">{r.vehicle_count}</td>
                <td className="pr-3 text-right num">{num(r.hours)}</td>
                <td className="text-right num font-semibold text-amber-300">{eur(r.total_cost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="md:hidden space-y-3">
        {rows.map((r) => (
          <Link key={r.site_id} to={`/cantieri/${r.site_id}`} className="block rounded-lg border border-border p-4 bg-background/40">
            <div className="flex justify-between"><span className="font-semibold">{r.site_name}</span><span className="num text-amber-300 font-semibold">{eur(r.total_cost)}</span></div>
            <div className="text-xs text-muted-foreground mt-1">{r.manager_name}</div>
            <div className="flex gap-4 mt-3 text-sm num"><span>{r.worker_count} operai</span><span>{r.vehicle_count} mezzi</span><span>{num(r.hours)} h</span></div>
          </Link>
        ))}
      </div>
    </>
  );
}

function KpiGrid({ k, isAdmin }) {
  const nav = useNavigate();
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mb-3">
        <Kpi label="Cantieri attivi" value={k.sites_active} icon={Building2} accent="bg-emerald-500" testid="kpi-sites-active" onClick={() => nav("/cantieri")} />
        <Kpi label="Da avviare" value={k.sites_to_start} icon={PlayCircle} accent="bg-slate-400" testid="kpi-sites-to-start" onClick={() => nav("/cantieri")} />
        <Kpi label="Sospesi" value={k.sites_suspended} icon={PauseCircle} accent="bg-amber-500" testid="kpi-sites-suspended" onClick={() => nav("/cantieri")} />
        <Kpi label="Operai totali" value={k.workers_total} icon={HardHat} accent="bg-sky-500" testid="kpi-workers-total" onClick={isAdmin ? () => nav("/operai") : undefined} />
        <Kpi label="Disponibili oggi" value={k.workers_available} icon={UserCheck} accent="bg-emerald-500" testid="kpi-workers-available" hint={`${k.workers_absent} assenti`} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mb-6">
        <Kpi label="Operai impegnati" value={k.workers_busy} icon={UserX} accent="bg-red-500" testid="kpi-workers-busy" />
        <Kpi label="Mezzi disponibili" value={k.vehicles_available} icon={Truck} accent="bg-emerald-500" testid="kpi-vehicles-available" onClick={isAdmin ? () => nav("/mezzi") : undefined} />
        <Kpi label="Mezzi in cantiere" value={k.vehicles_on_site} icon={MapPin} accent="bg-sky-500" testid="kpi-vehicles-on-site" />
        <Kpi label="In manutenzione" value={k.vehicles_maintenance} icon={Wrench} accent="bg-orange-500" testid="kpi-vehicles-maintenance" />
        <Kpi label="Scadenze 30 gg" value={k.deadlines_30} icon={CalendarClock} accent="bg-red-500" testid="kpi-deadlines" />
      </div>
    </>
  );
}

function TodaySection({ rows, k }) {
  const nav = useNavigate();
  return (
    <section className="surface p-4 sm:p-6 min-w-0 overflow-hidden">
      <div className="flex items-center justify-between mb-4">
        <div><div className="eyebrow">Oggi</div><h2 className="text-lg font-semibold">Attività di oggi</h2></div>
        <Button variant="outline" size="sm" onClick={() => nav("/operativa")} data-testid="goto-operations-btn">Dashboard operativa <ChevronRight className="h-4 w-4 ml-1" /></Button>
      </div>
      <TodayTable rows={rows} />
      <div className="grid grid-cols-2 gap-3 mt-5">
        <div className="rounded-lg border border-border p-3"><div className="eyebrow flex items-center gap-1"><Euro className="h-3 w-3" />Costo settimana</div><div className="num text-xl font-semibold mt-1" data-testid="cost-week">{eur(k.cost_week)}</div></div>
        <div className="rounded-lg border border-border p-3"><div className="eyebrow flex items-center gap-1"><Euro className="h-3 w-3" />Costo mese</div><div className="num text-xl font-semibold mt-1" data-testid="cost-month">{eur(k.cost_month)}</div></div>
      </div>
    </section>
  );
}

const DeadlinesSection = ({ items }) => (
  <section className="surface p-4 sm:p-6 min-w-0 overflow-hidden">
    <div className="eyebrow">Scadenzario</div>
    <h2 className="text-lg font-semibold mb-2">Prossime scadenze</h2>
    <p className="text-xs text-muted-foreground mb-2">Patentini, attestati, idoneità mediche, revisioni, assicurazioni e bolli.</p>
    <DeadlineList items={items} />
  </section>
);

const MySites = ({ sites }) => (
  <section className="surface p-4 sm:p-6 mt-6">
    <h2 className="text-lg font-semibold mb-4">I miei cantieri</h2>
    <div className="grid sm:grid-cols-2 gap-3">
      {sites.map((s) => (
        <Link key={s.id} to={`/cantieri/${s.id}`} className="rounded-lg border border-border p-4 hover:border-amber-500/60 transition-colors" data-testid={`my-site-${s.id}`}>
          <div className="flex justify-between gap-2"><span className="font-semibold">{s.name}</span><SiteStatusBadge status={s.status} /></div>
          <div className="text-xs text-muted-foreground mt-1">{s.address}</div>
        </Link>
      ))}
    </div>
  </section>
);

function StaffDashboard({ data, isAdmin }) {
  return (
    <>
      {isAdmin && <BudgetAlertsBanner items={data.budget_alerts} />}
      <KpiGrid k={data.kpis} isAdmin={isAdmin} />
      <div className="grid xl:grid-cols-[1.6fr_1fr] gap-6">
        <TodaySection rows={data.today} k={data.kpis} />
        <DeadlinesSection items={data.deadlines} />
      </div>
      {!isAdmin && data.my_sites?.length > 0 && <MySites sites={data.my_sites} />}
    </>
  );
}

function WorkerHome({ data }) {
  const nav = useNavigate();
  const siteCount = new Set(data.today.map((a) => a.site_id)).size;
  return (
    <div className="space-y-5 max-w-3xl">
      <div className="surface p-5 flex items-center justify-between gap-3">
        <div><div className="eyebrow">Il mio stato oggi</div><div className="mt-2"><AvailBadge status={data.status?.status} testid="my-status" /></div></div>
        <Button variant="outline" className="h-12" onClick={() => nav("/notifiche")} data-testid="home-notifications-btn">
          <Bell className="h-5 w-5 mr-2" /> {data.unread_notifications} notifiche
        </Button>
      </div>
      <section className="surface p-5 border-amber-500/40" data-testid="today-shift-card">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="eyebrow text-amber-400">{siteCount > 1 ? `Oggi lavori in ${siteCount} cantieri` : "Turno di oggi"}</div>
          {data.today.length > 0 && <div className="num text-sm font-semibold" data-testid="today-total-hours">{data.today_off ? `${data.today_off}: 0 ore` : `${num(data.today_hours)} ore in totale`}</div>}
        </div>
        {data.today.length ? data.today.map((a) => (
          <div key={a.id} className="mt-3 pt-3 border-t border-border/60 first-of-type:border-0 first-of-type:pt-0" data-testid={`today-shift-${a.id}`}>
            <div className="text-2xl font-bold font-heading">{a.site_name}</div>
            <div className="text-muted-foreground flex items-center gap-2 mt-1"><MapPin className="h-4 w-4" />{a.address}</div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="rounded-lg bg-background/60 border border-border p-3"><div className="eyebrow">Orario</div><div className="num text-base sm:text-lg mt-1 flex items-center gap-2 whitespace-nowrap"><Clock className="h-4 w-4 shrink-0" />{a.shift}</div></div>
              <div className="rounded-lg bg-background/60 border border-border p-3"><div className="eyebrow">Capo cantiere</div><div className="text-sm mt-1">{a.manager}</div></div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              {a.manager_phone && <a href={`tel:${a.manager_phone}`} className="h-12 rounded-lg bg-amber-500 text-slate-950 font-bold flex items-center justify-center gap-2" data-testid="call-manager-btn"><Phone className="h-5 w-5" />Chiama</a>}
              <a href={`https://maps.google.com/?q=${encodeURIComponent(a.address || "")}`} target="_blank" rel="noreferrer" className="h-12 rounded-lg border border-border font-semibold flex items-center justify-center gap-2" data-testid="open-maps-btn"><MapPin className="h-5 w-5" />Naviga</a>
            </div>
          </div>
        )) : <div className="mt-3 text-muted-foreground">Nessun turno assegnato per oggi.</div>}
      </section>
      <section className="surface p-5">
        <div className="flex justify-between items-center mb-3"><h2 className="text-lg font-semibold">Prossimi turni</h2><Link to="/turni" className="text-sm text-amber-400">Vedi tutti</Link></div>
        {data.upcoming.length ? (
          <ul className="space-y-2">
            {data.upcoming.slice(0, 5).map((a) => (
              <li key={a.id} className="rounded-lg border border-border p-3 flex justify-between gap-3">
                <div><div className="font-semibold">{a.site_name}</div><div className="text-xs text-muted-foreground">{a.shift}</div></div>
                <div className="text-sm num text-right">{fmtDate(a.start_date)}<br />{fmtDate(a.end_date)}</div>
              </li>
            ))}
          </ul>
        ) : <Empty title="Nessun turno pianificato" />}
      </section>
      <section className="surface p-5"><h2 className="text-lg font-semibold mb-2">Le mie scadenze</h2><DeadlineList items={data.deadlines} /></section>
    </div>
  );
}

function DashboardBody({ data, loading, role }) {
  if (loading || !data) return <Loading />;
  if (role === "operaio") return <WorkerHome data={data} />;
  return <StaffDashboard data={data} isAdmin={role === "admin"} />;
}

export default function Dashboard() {
  const { user } = useAuth();
  const { data, loading } = useApi("/dashboard");
  const titles = { admin: "Dashboard generale", responsabile: "Dashboard responsabile", operaio: `Ciao ${user.first_name}` };
  return (
    <div>
      <PageHeader eyebrow="Cantiere Manager Pro" title={titles[user.role]}
        subtitle={user.role === "operaio" ? "I tuoi turni e le tue comunicazioni" : "Situazione in tempo reale di cantieri, operai e mezzi"} />
      <DashboardBody data={data} loading={loading} role={user.role} />
    </div>
  );
}
